import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

const MILESTONES = [
  { days: 7,   label: 'Bronze',  emoji: '🥉', color: 'from-amber-600 to-amber-800',  text: 'text-amber-300',  bg: 'bg-amber-500/20 border-amber-500/30',  bgLight: 'bg-amber-50 border-amber-200',  textLight: 'text-amber-700' },
  { days: 30,  label: 'Silver',  emoji: '🥈', color: 'from-slate-400 to-slate-600',  text: 'text-slate-300',  bg: 'bg-slate-500/20 border-slate-500/30',  bgLight: 'bg-slate-50 border-slate-200',  textLight: 'text-slate-600' },
  { days: 100, label: 'Gold',    emoji: '🥇', color: 'from-yellow-400 to-amber-500', text: 'text-yellow-300', bg: 'bg-yellow-500/20 border-yellow-500/30', bgLight: 'bg-yellow-50 border-yellow-200', textLight: 'text-yellow-700' },
  { days: 365, label: 'Diamond', emoji: '💎', color: 'from-cyan-400 to-blue-500',    text: 'text-cyan-300',  bg: 'bg-cyan-500/20 border-cyan-500/30',    bgLight: 'bg-cyan-50 border-cyan-200',    textLight: 'text-cyan-700' },
];

const BADGE_SEEN_KEY = 'badge_seen_';

/**
 * StreakBadges — shows earned badges + next milestone progress.
 * Animates in a "badge unlocked" notification on first time reaching a milestone.
 */
const StreakBadges = ({ streak }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const [newBadge, setNewBadge] = useState(null);

  const earned = MILESTONES.filter(m => streak >= m.days);
  const current = earned[earned.length - 1] || null;
  const next = MILESTONES.find(m => streak < m.days) || null;
  const progress = next ? Math.round((streak / next.days) * 100) : 100;

  // Check for newly unlocked badge
  useEffect(() => {
    if (!current) return;
    const key = BADGE_SEEN_KEY + current.days;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, '1');
      setNewBadge(current);
      setTimeout(() => setNewBadge(null), 4000);
    }
  }, [current]);

  if (streak === 0) return null;

  return (
    <div className={`rounded-2xl p-4 ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}
      dir={isRTL ? 'rtl' : 'ltr'}>

      {/* New badge unlock notification */}
      {newBadge && (
        <div className={`badge-unlock mb-3 flex items-center gap-3 p-3 rounded-xl border ${
          isDarkMode ? newBadge.bg : newBadge.bgLight
        } ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-2xl">{newBadge.emoji}</span>
          <div className={isRTL ? 'text-right' : ''}>
            <p className={`text-xs font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              {t('home.badgeUnlocked')}
            </p>
            <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t(`home.badge${newBadge.label}`)} — {newBadge.days} {t('home.days')}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('home.streakProgress')}
        </span>
        {current && (
          <span className={`flex items-center gap-1.5 text-xs font-semibold ${isDarkMode ? current.text : current.textLight}`}>
            <span>{current.emoji}</span>
            {t(`home.badge${current.label}`)}
          </span>
        )}
      </div>

      {/* Badge row */}
      <div className="flex items-center justify-between mb-4">
        {MILESTONES.map((m, i) => {
          const isEarned = streak >= m.days;
          const isNext = !isEarned && (i === 0 || streak >= MILESTONES[i - 1].days);
          return (
            <div key={m.days} className="flex flex-col items-center gap-1">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all ${
                isEarned
                  ? `bg-gradient-to-br ${m.color} shadow-md`
                  : isNext
                    ? isDarkMode ? 'bg-white/10 border-2 border-dashed border-white/30' : 'bg-gray-50 border-2 border-dashed border-gray-300'
                    : isDarkMode ? 'bg-white/5 opacity-35' : 'bg-gray-50 opacity-40'
              }`}>
                <span className={`text-lg ${!isEarned && !isNext ? 'grayscale opacity-50' : ''}`}>
                  {isEarned ? m.emoji : '🔒'}
                </span>
              </div>
              <span className={`text-[9px] font-semibold ${
                isEarned ? isDarkMode ? m.text : m.textLight : isDarkMode ? 'text-gray-300' : 'text-gray-400'
              }`}>
                {m.days}d
              </span>
            </div>
          );
        })}
      </div>

      {/* Progress to next milestone */}
      {next && (
        <div>
          <div className="flex justify-between text-[10px] mb-1.5">
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-400'}>
              {streak} / {next.days} {t('home.days')}
            </span>
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              {next.days - streak} {t('home.days')} {t('home.nextMilestone')} {next.emoji}
            </span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
            <div
              className={`h-full rounded-full bg-gradient-to-r ${next.color}`}
              style={{ width: `${progress}%`, transition: 'width 0.8s ease' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StreakBadges;
